import type { Device, WarningType as APIWarningType } from '../types';

import { isBatteryLow } from './deviceUtils';

/**
 * Maintenance Utilities
 *
 * Functions for detecting and managing device maintenance warnings
 */

/**
 * Human-readable labels for warning types from the API
 * Used by Maintenance views (DeviceView, BeaconView, CheckinView)
 */
export const WARNING_TYPE_LABELS: Record<APIWarningType, string> = {
  battery_low: '🪫 Low battery',
  signal_weak: '📡 Weak signal',
  no_signal: '📡 No signal',
  temperature_high: '🌡️ High temperature',
  temperature_low: '🌡️ Low temperature',
  humidity_high: '💧 High humidity',
  humidity_low: '💧 Low humidity',
  relay_offline: '🔌 Relay offline',
};

export type WarningType = 'battery' | 'signal' | 'no_signal';
export type WarningSeverity = 'critical' | 'warning';

export interface DeviceWarning {
  type: WarningType;
  severity: WarningSeverity;
  message: string;
  value: number;
  timestamp: Date | string | null;
}

export interface WarningSummary {
  total: number;
  critical: number;
  warning: number;
  unacknowledged: number;
  byType: Partial<Record<WarningType, number>>;
}

/** Device with optional maintenance acknowledgement field */
export type MaintenanceDevice = Partial<Device> & {
  maintenanceAcknowledgedAt?: Date | string | null;
};

/**
 * Check if device has any maintenance warnings
 * @param {Object} device - Device object
 * @returns {boolean} True if device has warnings
 */
export const hasMaintenanceWarnings = (device: MaintenanceDevice): boolean => {
  const warnings = getDeviceWarnings(device);
  return warnings.length > 0;
};

/**
 * Get locally-computable warnings for a device (battery only).
 * No-signal warnings are generated server-side and delivered via the API.
 * @param {Object} device - Device object
 * @returns {Array} Array of warning objects
 */
export const getDeviceWarnings = (device: MaintenanceDevice): DeviceWarning[] => {
  const warnings: DeviceWarning[] = [];

  if (isBatteryLow(device)) {
    warnings.push({
      type: 'battery',
      severity: 'critical',
      message: `Lågt batteri: ${device.output?.volts ? Math.round(device.output.volts * 10) / 10 + 'V' : 'okänd spänning'}`,
      value: device.output?.volts || 0,
      timestamp: device.timestamp || null,
    });
  }

  return warnings;
};

/**
 * Get count of devices with warnings
 * @param {Array} devices - Array of device objects
 * @returns {number} Count of devices with warnings
 */
export const getWarningCount = (devices: MaintenanceDevice[]): number => {
  if (!Array.isArray(devices)) return 0;
  return devices.filter((device) => hasMaintenanceWarnings(device)).length;
};

/**
 * Get count of devices with critical warnings
 * @param {Array} devices - Array of device objects
 * @returns {number} Count of devices with critical warnings
 */
export const getCriticalWarningCount = (devices: MaintenanceDevice[]): number => {
  if (!Array.isArray(devices)) return 0;
  return devices.filter((device) => {
    const warnings = getDeviceWarnings(device);
    return warnings.some((w) => w.severity === 'critical');
  }).length;
};

/**
 * Get count of unacknowledged warnings
 * @param {Array} devices - Array of device objects
 * @returns {number} Count of devices with unacknowledged warnings
 */
export const getUnacknowledgedWarningCount = (devices: MaintenanceDevice[]): number => {
  if (!Array.isArray(devices)) return 0;
  return devices.filter((device) => {
    return hasMaintenanceWarnings(device) && !device.maintenanceAcknowledgedAt;
  }).length;
};

/**
 * Check if device has critical warnings
 * @param {Object} device - Device object
 * @returns {boolean} True if device has critical warnings
 */
export const hasCriticalWarnings = (device: MaintenanceDevice): boolean => {
  const warnings = getDeviceWarnings(device);
  return warnings.some((w) => w.severity === 'critical');
};

/**
 * Get warning summary for all devices
 * @param {Array} devices - Array of device objects
 * @returns {Object} Warning summary statistics
 */
export const getWarningSummary = (devices: MaintenanceDevice[]): WarningSummary => {
  if (!Array.isArray(devices)) {
    return { total: 0, critical: 0, warning: 0, unacknowledged: 0, byType: {} };
  }

  const summary: WarningSummary = {
    total: 0,
    critical: 0,
    warning: 0,
    unacknowledged: 0,
    byType: {},
  };

  devices.forEach((device) => {
    const warnings = getDeviceWarnings(device);
    if (warnings.length > 0) {
      summary.total++;

      if (warnings.some((w) => w.severity === 'critical')) {
        summary.critical++;
      } else {
        summary.warning++;
      }

      if (!device.maintenanceAcknowledgedAt) {
        summary.unacknowledged++;
      }

      warnings.forEach((w) => {
        summary.byType[w.type] = (summary.byType[w.type] ?? 0) + 1;
      });
    }
  });

  return summary;
};
